import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const KIT_BASE = "https://api.kit.com/v4";

// -----------------------------------------------------------------------------
// DEVELOPMENT-ONLY SAFETY SHIM  (remixed dev copy of Tables and Bridges)
// -----------------------------------------------------------------------------
// This project is a development remix. All Kit-connected server functions
// short-circuit BEFORE any network call to Kit or any owner/submitter email
// enqueue, so submissions from this project cannot create or update real Kit
// subscribers, tags, custom fields, or send transactional email.
//
// The original integration code is preserved below the guard so it can be
// restored at final launch by setting the env var KIT_LIVE=true (and providing
// KIT_API_KEY). Do NOT flip this on in the dev project.
// -----------------------------------------------------------------------------
function isKitLive(): boolean {
  return process.env.KIT_LIVE === "true";
}
function devModeMockResult() {
  console.info("[kit dev-mode] submission accepted locally; not forwarded to Kit");
  return { ok: true as const, subscriberId: 0, devMode: true as const };
}

// -----------------------------------------------------------------------------
// KIT SEGMENTATION MAP  (single source of truth for tag names)
// -----------------------------------------------------------------------------
// These are the exact tag names that will be applied to Kit subscribers when
// KIT_LIVE=true. Before flipping KIT_LIVE on, each tag below MUST be
// pre-created in Kit with the identical spelling and spacing. No CEO Suite
// tag exists on this site by design.
//
// Recommended sequence mapping (configure in Kit, not here):
//   TB - CEO Practitioner Network Waitlist -> CEO Practitioner Network Waitlist Welcome
//   TB - Podcast Updates           -> Podcast Launch Updates
//   TB - Agency Services Inquiry   -> internal notification + short ack email
//   TB - Book Carlai Inquiry       -> internal notification + short ack email
//   TB - General Updates           -> (no sequence)
// -----------------------------------------------------------------------------
export const KIT_TAGS = {
  ceoPractitionerNetworkWaitlist: "TB - CEO Practitioner Network Waitlist",
  podcastUpdates: "TB - Podcast Updates",
  agencyInquiry: "TB - Agency Services Inquiry",
  organizationInquiry: "TB - Organization Services Inquiry",
  bookCarlaiInquiry: "TB - Book Carlai Inquiry",
  generalUpdates: "TB - General Updates",
  guideWaitlist: "TB - Engagement Guide Waitlist",
  conferenceWaitlist: "TB - Conference Waitlist",

} as const;

const subscribeSchema = z.object({
  email: z.string().trim().toLowerCase().email().max(255),
  tag: z.string().trim().min(1).max(64),
  firstName: z.string().trim().max(100).optional(),
  /** Route the signup came from, stored with the submission for attribution. */
  sourcePage: z.string().trim().max(200).optional(),
   fields: z.record(z.string().max(2000)).optional(),
 }).superRefine((data, ctx) => {
   if (data.tag !== KIT_TAGS.organizationInquiry) return;
   for (const field of ["Name", "Title", "Organization", "Support"]) {
     if (!data.fields?.[field]?.trim()) ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["fields", field], message: `${field} is required.` });
   }
   const support = data.fields?.Support;
   if (support && !["Keynote", "Workshop", "Team or leadership training", "Facilitation", "Consulting", "Not sure yet"].includes(support)) {
     ctx.addIssue({ code: z.ZodIssueCode.custom, path: ["fields", "Support"], message: "Choose a type of support." });
   }
 });

const contactSchema = z.object({
  name: z.string().trim().min(1).max(100),
  email: z.string().trim().toLowerCase().email().max(255),
  message: z.string().trim().min(1).max(2000),
  honeypot: z.string().max(200).optional().default(""),
  submittedAt: z.number().int().nonnegative(),
});

export const speakingSchema = z.object({
  firstName: z.string().trim().min(1).max(100),
  lastName: z.string().trim().min(1).max(100),
  email: z.string().trim().toLowerCase().email().max(255),
  phone: z.string().trim().min(1).max(40),
  company: z.string().trim().max(200).optional().default(""),
  preferredContact: z.enum(["Phone", "Text", "Email"]),
  smsConsent: z.boolean().optional().default(false),
  eventName: z.string().trim().max(200).optional().default(""),
  eventDates: z.string().trim().min(1).max(200),
  speakerBudget: z.string().trim().min(1).max(80),
  venue: z.string().trim().max(500).optional().default(""),
  audience: z.string().trim().max(500).optional().default(""),
  attendees: z.string().trim().max(40).optional().default(""),
  duration: z.string().trim().max(80).optional().default(""),
  topic: z.string().trim().max(2000).optional().default(""),
  additionalInfo: z.string().trim().max(2000).optional().default(""),
});

const villageApplicationSchema = z.object({
  fullName: z.string().trim().min(1).max(200),
  agency: z.string().trim().min(1).max(200),
  role: z.string().trim().min(1).max(200),
  state: z.string().trim().min(2).max(60),
  lecenMember: z.enum(["Yes", "No"]),
  email: z.string().trim().toLowerCase().email().max(255),
  whyVillage: z.string().trim().min(1).max(2000),
  honeypot: z.string().max(200).optional().default(""),
  submittedAt: z.number().int().nonnegative(),
});

const engagementSchema = z.object({
  firstName: z.string().trim().min(1).max(100),
  lastName: z.string().trim().min(1).max(100),
  email: z.string().trim().toLowerCase().email().max(255),
  phone: z.string().trim().min(1).max(40),
  company: z.string().trim().min(1).max(200),
  role: z.string().trim().max(200).optional().default(""),
  preferredContact: z.enum(["Phone", "Text", "Email"]),
  smsConsent: z.boolean().optional().default(false),
  engagementType: z.enum([
    "Keynote",
    "Workshop",
    "Panel",
    "Strategy Intensive",
    "Multi-day Engagement",
    "Other",
  ]),
  eventName: z.string().trim().max(200).optional().default(""),
  eventDates: z.string().trim().min(1).max(200),
  location: z.string().trim().max(500).optional().default(""),
  audienceSize: z.string().trim().max(40).optional().default(""),
  sessionLength: z.string().trim().max(80).optional().default(""),
  budgetRange: z.enum([
    "Under $5,000",
    "$5,000 – $10,000",
    "$10,000 – $20,000",
    "$20,000 – $50,000",
    "$50,000+",
    "Let's discuss",
  ]),
  audienceTakeaway: z.string().trim().min(1).max(2000),
  constraints: z.string().trim().max(2000).optional().default(""),
  howHeard: z.string().trim().max(200).optional().default(""),
  honeypot: z.string().max(200).optional().default(""),
  submittedAt: z.number().int().nonnegative(),
});

async function kitFetch(path: string, init: RequestInit, apiKey: string) {
  const res = await fetch(`${KIT_BASE}${path}`, {
    ...init,
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      "X-Kit-Api-Key": apiKey,
      ...(init.headers || {}),
    },
  });
  const text = await res.text();
  let json: unknown = null;
  try {
    json = text ? JSON.parse(text) : null;
  } catch {
    json = { raw: text };
  }
  if (!res.ok) {
    console.error("Kit API error", res.status, path, json);
    throw new Error("Service unavailable. Please try again later.");
  }
  return json as Record<string, unknown>;
}

async function ensureTagId(tagName: string, apiKey: string): Promise<number> {
  const list = (await kitFetch(`/tags?per_page=500`, { method: "GET" }, apiKey)) as {
    tags?: Array<{ id: number; name: string }>;
  };
  const existing = list.tags?.find((t) => t.name.toLowerCase() === tagName.toLowerCase());
  if (existing) return existing.id;
  const created = (await kitFetch(
    `/tags`,
    { method: "POST", body: JSON.stringify({ name: tagName }) },
    apiKey,
  )) as { tag?: { id: number } };
  if (!created.tag?.id) {
    console.error("Tag creation failed", tagName, created);
    throw new Error("Service unavailable. Please try again later.");
  }
  return created.tag.id;
}

async function ensureCustomFields(labels: string[], apiKey: string): Promise<void> {
  if (labels.length === 0) return;
  const list = (await kitFetch(`/custom_fields?per_page=500`, { method: "GET" }, apiKey)) as {
    custom_fields?: Array<{ id: number; label: string; key: string }>;
  };
  const existing = new Set((list.custom_fields ?? []).map((f) => f.label.toLowerCase()));
  for (const label of labels) {
    if (existing.has(label.toLowerCase())) continue;
    try {
      await kitFetch(
        `/custom_fields`,
        { method: "POST", body: JSON.stringify({ label }) },
        apiKey,
      );
    } catch (err) {
      console.warn("Could not create custom field", label, err);
    }
  }
}

async function subscribeInternal(input: z.infer<typeof subscribeSchema>, apiKey: string) {
  const subBody: Record<string, unknown> = {
    email_address: input.email,
    state: "active",
  };
  if (input.firstName) subBody.first_name = input.firstName;
  if (input.fields) subBody.fields = input.fields;

  const subRes = (await kitFetch(
    `/subscribers`,
    { method: "POST", body: JSON.stringify(subBody) },
    apiKey,
  )) as { subscriber?: { id: number; email_address: string } };

  const subscriberId = subRes.subscriber?.id;
  if (!subscriberId) {
    console.error("Subscriber creation returned no id", subRes);
    throw new Error("Service unavailable. Please try again later.");
  }

  const tagId = await ensureTagId(input.tag, apiKey);
  await kitFetch(
    `/tags/${tagId}/subscribers/${subscriberId}`,
    { method: "POST", body: JSON.stringify({}) },
    apiKey,
  );

  return { ok: true as const, subscriberId };
}

export const subscribeToKit = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => subscribeSchema.parse(data))
  .handler(async ({ data }) => {
    const { captureSubmission } = await import("@/lib/submissions-capture.server");
    const isConference = data.tag === KIT_TAGS.conferenceWaitlist;
    const isOrganization = data.tag === KIT_TAGS.organizationInquiry;
    const isAgency = data.tag === KIT_TAGS.agencyInquiry;
    const formType =
      data.tag === KIT_TAGS.podcastUpdates
        ? "podcast_updates"
        : isConference
          ? "conference_waitlist"
          : isOrganization ? "organization_inquiry" : isAgency ? "agency_inquiry" : "general_subscribe";
    const sourcePage = data.sourcePage ?? (isConference ? "/conference" : isOrganization ? "/for-organizations" : isAgency ? "/for-agencies" : null);
    if (!isKitLive()) {
      await captureSubmission({
        formType, sourcePage, email: data.email, firstName: data.firstName ?? null,
        kitTag: data.tag, payload: data as unknown as Record<string, unknown>,
        kitStatus: "dev_mode",
      });
      return devModeMockResult();
    }
    const apiKey = process.env.KIT_API_KEY;
    if (!apiKey) { console.error("KIT_API_KEY is not configured"); throw new Error("Service unavailable. Please try again later."); };
    if (data.fields) await ensureCustomFields(Object.keys(data.fields), apiKey);
    try {
      const result = await subscribeInternal(data, apiKey);
      await captureSubmission({
        formType, sourcePage, email: data.email, firstName: data.firstName ?? null,
        kitTag: data.tag, payload: data as unknown as Record<string, unknown>,
        kitStatus: "sent", kitSubscriberId: result.subscriberId,
      });
      // Conference waitlist signups get an immediate confirmation email.
      // Sends are retried with backoff and every outcome (queued / failed) is
      // written to email_send_log. A failure here must not fail the signup.
      if (isConference) {
        try {
          const { sendInternalNotificationWithRetry } = await import(
            "@/lib/email/send-with-retry"
          );
          const confirm = await sendInternalNotificationWithRetry({
            templateName: "conference-waitlist-confirmation",
            recipientEmail: data.email,
            templateData: { firstName: data.firstName ?? "", email: data.email },
            // Stable key: retries and duplicate submissions cannot double-send.
            idempotencyKey: `conference-waitlist-${data.email}`,
            maxAttempts: 3,
          });
          if (!confirm.ok) {
            console.error(
              `[conference waitlist] confirmation email failed after ${confirm.attempts} attempts: ${confirm.message} (${confirm.reason})`,
            );
          }
        } catch (mailErr) {
          console.error("[conference waitlist] confirmation email threw", mailErr);
        }
      }

      if (isOrganization || isAgency) {
        try {
          const { enqueueInternalNotification } = await import("@/lib/email/send-internal");
          const notification = await enqueueInternalNotification({
            templateName: "service-inquiry-notification",
            idempotencyKey: `${formType}-${data.email}-${JSON.stringify(data.fields ?? {})}`,
            templateData: {
              name: data.fields?.Name ?? data.firstName ?? "Website visitor",
              email: data.email,
              inquiryType: isOrganization ? "organization" : "agency",
              message: Object.entries(data.fields ?? {}).map(([label, value]) => `${label}: ${value}`).join("\n\n"),
            },
          });
          if (!notification.ok) console.warn("Service inquiry notification skipped", { reason: notification.reason });
        } catch (mailErr) {
          console.warn("Service inquiry notification failed", { error: mailErr instanceof Error ? mailErr.message : String(mailErr) });
        }
      }
      return result;
    } catch (err) {
      await captureSubmission({
        formType, sourcePage, email: data.email, firstName: data.firstName ?? null,
        kitTag: data.tag, payload: data as unknown as Record<string, unknown>,
        kitStatus: "error", kitError: err instanceof Error ? err.message : String(err),
      });
      throw err;
    }
  });

export const submitContact = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => contactSchema.parse(data))
  .handler(async ({ data }) => {
    if (data.honeypot && data.honeypot.trim().length > 0) throw new Error("Unable to send message");
    if (Date.now() - data.submittedAt < 3000) throw new Error("Unable to send message");
    const { captureSubmission } = await import("@/lib/submissions-capture.server");
    const basePayload = data as unknown as Record<string, unknown>;
    if (!isKitLive()) {
      await captureSubmission({
        formType: "contact", email: data.email, firstName: data.name,
        kitTag: KIT_TAGS.generalUpdates, payload: basePayload, kitStatus: "dev_mode",
      });
      return devModeMockResult();
    }
    const apiKey = process.env.KIT_API_KEY;
    if (!apiKey) { console.error("KIT_API_KEY is not configured"); throw new Error("Service unavailable. Please try again later."); };
    await ensureCustomFields(["Contact Message"], apiKey);
    let result;
    try {
      result = await subscribeInternal(
        { email: data.email, tag: KIT_TAGS.generalUpdates, firstName: data.name,
          fields: { "Contact Message": data.message } }, apiKey,
      );
      await captureSubmission({
        formType: "contact", email: data.email, firstName: data.name,
        kitTag: KIT_TAGS.generalUpdates, payload: basePayload, kitStatus: "sent",
        kitSubscriberId: result.subscriberId,
      });
    } catch (err) {
      await captureSubmission({
        formType: "contact", email: data.email, firstName: data.name,
        kitTag: KIT_TAGS.generalUpdates, payload: basePayload, kitStatus: "error",
        kitError: err instanceof Error ? err.message : String(err),
      });
      throw err;
    }

    // Fire-and-forget owner notification — never let an email enqueue
    // failure surface to the visitor or block the form.
    try {
      const { enqueueInternalNotification } = await import("@/lib/email/send-internal");
      const notif = await enqueueInternalNotification({
        templateName: "contact-inquiry-notification",
        idempotencyKey: `contact-${data.email}-${data.submittedAt}`,
        templateData: { name: data.name, email: data.email, message: data.message },
      });
      if (!notif.ok) console.warn("Contact notification enqueue skipped", { reason: notif.reason });
    } catch (err) {
      console.warn("Contact notification enqueue threw", { error: err instanceof Error ? err.message : String(err) });
    }

    return result;
  });

const SPEAKING_FIELDS = [
  "Last Name",
  "Phone",
  "Company",
  "Preferred Contact",
  "SMS Consent",
  "Event Name",
  "Event Dates",
  "Speaker Budget",
  "Venue",
  "Audience",
  "Attendees",
  "Duration",
  "Topic",
  "Additional Info",
];

export const submitSpeakingInquiry = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => speakingSchema.parse(data))
  .handler(async ({ data }) => {
    const { captureSubmission } = await import("@/lib/submissions-capture.server");
    const basePayload = data as unknown as Record<string, unknown>;
    const capFields = {
      formType: "speaking_inquiry", email: data.email, firstName: data.firstName,
      lastName: data.lastName, phone: data.phone, company: data.company,
      kitTag: KIT_TAGS.bookCarlaiInquiry, payload: basePayload,
    } as const;
    if (!isKitLive()) {
      await captureSubmission({ ...capFields, kitStatus: "dev_mode" });
      return devModeMockResult();
    }
    const apiKey = process.env.KIT_API_KEY;
    if (!apiKey) { console.error("KIT_API_KEY is not configured"); throw new Error("Service unavailable. Please try again later."); };
    await ensureCustomFields(SPEAKING_FIELDS, apiKey);
    let result;
    try {
      result = await subscribeInternal(
        { email: data.email, tag: KIT_TAGS.bookCarlaiInquiry, firstName: data.firstName,
          fields: {
            "Last Name": data.lastName, Phone: data.phone, Company: data.company,
            "Preferred Contact": data.preferredContact,
            "SMS Consent": data.smsConsent ? "Yes" : "No",
            "Event Name": data.eventName, "Event Dates": data.eventDates,
            "Speaker Budget": data.speakerBudget, Venue: data.venue,
            Audience: data.audience, Attendees: data.attendees, Duration: data.duration,
            Topic: data.topic, "Additional Info": data.additionalInfo,
          } }, apiKey,
      );
      await captureSubmission({ ...capFields, kitStatus: "sent", kitSubscriberId: result.subscriberId });
    } catch (err) {
      await captureSubmission({ ...capFields, kitStatus: "error", kitError: err instanceof Error ? err.message : String(err) });
      throw err;
    }

    // Fire-and-forget owner notification — failures never block the form.
    try {
      const { enqueueInternalNotification } = await import("@/lib/email/send-internal");
      const idemSource = `${data.email}|${data.eventName}|${data.eventDates}`;
      const notif = await enqueueInternalNotification({
        templateName: "speaking-booking-notification",
        idempotencyKey: `speaking-${idemSource}`,
        templateData: {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone,
          company: data.company,
          preferredContact: data.preferredContact,
          smsConsent: data.smsConsent,
          eventName: data.eventName,
          eventDates: data.eventDates,
          speakerBudget: data.speakerBudget,
          venue: data.venue,
           audience: data.audience,
          attendees: data.attendees,
          duration: data.duration,
          topic: data.topic,
          additionalInfo: data.additionalInfo,
        },
      });
      if (!notif.ok) {
        console.warn("Speaking notification enqueue skipped", { reason: notif.reason });
      }
    } catch (err) {
      console.warn("Speaking notification enqueue threw", {
        error: err instanceof Error ? err.message : String(err),
      });
    }

    // Fire-and-forget submitter confirmation — failures never block the form.
    try {
      const { enqueueInternalNotification } = await import("@/lib/email/send-internal");
      const idemSource = `${data.email}|${data.eventName}|${data.eventDates}`;
      const confirm = await enqueueInternalNotification({
        templateName: "speaking-booking-confirmation",
        recipientEmail: data.email,
        idempotencyKey: `speaking-confirm-${idemSource}`,
        templateData: {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone,
          company: data.company,
          preferredContact: data.preferredContact,
          smsConsent: data.smsConsent,
          eventName: data.eventName,
          eventDates: data.eventDates,
          speakerBudget: data.speakerBudget,
          venue: data.venue,
           audience: data.audience,
          attendees: data.attendees,
          duration: data.duration,
          topic: data.topic,
          additionalInfo: data.additionalInfo,
        },
      });
      if (!confirm.ok) {
        console.warn("Speaking confirmation enqueue skipped", { reason: confirm.reason });
      }
    } catch (err) {
      console.warn("Speaking confirmation enqueue threw", {
        error: err instanceof Error ? err.message : String(err),
      });
    }

    return result;
  });

const VILLAGE_FIELDS = [
  "Last Name",
  "Agency",
  "Role",
  "State",
  "LECEN Member",
  "Why Village",
];

export const submitVillageApplication = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => villageApplicationSchema.parse(data))
  .handler(async ({ data }) => {
    if (data.honeypot && data.honeypot.trim().length > 0) {
      throw new Error("Unable to submit application");
    }
    if (Date.now() - data.submittedAt < 3000) {
      throw new Error("Unable to submit application");
    }
    const { captureSubmission } = await import("@/lib/submissions-capture.server");
    const parts = data.fullName.trim().split(/\s+/);
    const firstName = parts[0];
    const lastName = parts.slice(1).join(" ");
    const basePayload = data as unknown as Record<string, unknown>;
    const capFields = {
      formType: "village_application", email: data.email, firstName, lastName,
      kitTag: "village signup", payload: basePayload,
    } as const;
    if (!isKitLive()) {
      await captureSubmission({ ...capFields, kitStatus: "dev_mode" });
      return devModeMockResult();
    }
    const apiKey = process.env.KIT_API_KEY;
    if (!apiKey) { console.error("KIT_API_KEY is not configured"); throw new Error("Service unavailable. Please try again later."); };
    await ensureCustomFields(VILLAGE_FIELDS, apiKey);
    let result;
    try {
      result = await subscribeInternal(
        { email: data.email, tag: "village signup", firstName,
          fields: {
            "Last Name": lastName, Agency: data.agency, Role: data.role,
            State: data.state, "LECEN Member": data.lecenMember,
            "Why Village": data.whyVillage,
          } }, apiKey,
      );
      await captureSubmission({ ...capFields, kitStatus: "sent", kitSubscriberId: result.subscriberId });
    } catch (err) {
      await captureSubmission({ ...capFields, kitStatus: "error", kitError: err instanceof Error ? err.message : String(err) });
      throw err;
    }

    // Fire-and-forget owner notification — failures never block the form.
    try {
      const { enqueueInternalNotification } = await import("@/lib/email/send-internal");
      const notif = await enqueueInternalNotification({
        templateName: "village-application-notification",
        idempotencyKey: `village-${data.email}-${data.submittedAt}`,
        templateData: {
          fullName: data.fullName,
          email: data.email,
          agency: data.agency,
          role: data.role,
          state: data.state,
          lecenMember: data.lecenMember,
          whyVillage: data.whyVillage,
        },
      });
      if (!notif.ok) {
        console.warn("Village notification enqueue skipped", { reason: notif.reason });
      }
    } catch (err) {
      console.warn("Village notification enqueue threw", {
        error: err instanceof Error ? err.message : String(err),
      });
    }

    return result;
  });

const ENGAGEMENT_FIELDS = [
  "Last Name",
  "Phone",
  "Company",
  "Role",
  "Preferred Contact",
  "SMS Consent",
  "Engagement Type",
  "Event Name",
  "Event Dates",
  "Location",
  "Audience Size",
  "Session Length",
  "Budget Range",
  "Audience Takeaway",
  "Constraints",
  "How Heard",
];

export const submitEngagementInquiry = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => engagementSchema.parse(data))
  .handler(async ({ data }) => {
    if (data.honeypot && data.honeypot.trim().length > 0) {
      throw new Error("Unable to submit inquiry");
    }
    if (Date.now() - data.submittedAt < 3000) {
      throw new Error("Unable to submit inquiry");
    }
    const { captureSubmission } = await import("@/lib/submissions-capture.server");
    const basePayload = data as unknown as Record<string, unknown>;
    const capFields = {
      formType: "engagement_inquiry", email: data.email, firstName: data.firstName,
      lastName: data.lastName, phone: data.phone, company: data.company,
      kitTag: KIT_TAGS.agencyInquiry, payload: basePayload,
    } as const;
    if (!isKitLive()) {
      await captureSubmission({ ...capFields, kitStatus: "dev_mode" });
      return devModeMockResult();
    }
    const apiKey = process.env.KIT_API_KEY;
    if (!apiKey) { console.error("KIT_API_KEY is not configured"); throw new Error("Service unavailable. Please try again later."); }
    await ensureCustomFields(ENGAGEMENT_FIELDS, apiKey);
    let result;
    try {
      result = await subscribeInternal(
        { email: data.email, tag: KIT_TAGS.agencyInquiry, firstName: data.firstName,
          fields: {
            "Last Name": data.lastName, Phone: data.phone, Company: data.company,
            Role: data.role, "Preferred Contact": data.preferredContact,
            "SMS Consent": data.smsConsent ? "Yes" : "No",
            "Engagement Type": data.engagementType, "Event Name": data.eventName,
            "Event Dates": data.eventDates, Location: data.location,
            "Audience Size": data.audienceSize, "Session Length": data.sessionLength,
            "Budget Range": data.budgetRange, "Audience Takeaway": data.audienceTakeaway,
            Constraints: data.constraints, "How Heard": data.howHeard,
          } }, apiKey,
      );
      await captureSubmission({ ...capFields, kitStatus: "sent", kitSubscriberId: result.subscriberId });
    } catch (err) {
      await captureSubmission({ ...capFields, kitStatus: "error", kitError: err instanceof Error ? err.message : String(err) });
      throw err;
    }

    // Fire-and-forget owner notification — failures never block the form.
    try {
      const { enqueueInternalNotification } = await import("@/lib/email/send-internal");
      const notif = await enqueueInternalNotification({
        templateName: "engagement-inquiry-notification",
        idempotencyKey: `engagement-${data.email}-${data.submittedAt}`,
        templateData: {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone,
          company: data.company,
          role: data.role,
          preferredContact: data.preferredContact,
          smsConsent: data.smsConsent,
          engagementType: data.engagementType,
          eventName: data.eventName,
          eventDates: data.eventDates,
          location: data.location,
          audienceSize: data.audienceSize,
          sessionLength: data.sessionLength,
          budgetRange: data.budgetRange,
          audienceTakeaway: data.audienceTakeaway,
          constraints: data.constraints,
          howHeard: data.howHeard,
        },
      });
      if (!notif.ok) {
        console.warn("Engagement notification enqueue skipped", { reason: notif.reason });
      }
    } catch (err) {
      console.warn("Engagement notification enqueue threw", {
        error: err instanceof Error ? err.message : String(err),
      });
    }

    // Fire-and-forget submitter confirmation — failures never block the form.
    try {
      const { enqueueInternalNotification } = await import("@/lib/email/send-internal");
      const confirm = await enqueueInternalNotification({
        templateName: "engagement-inquiry-confirmation",
        recipientEmail: data.email,
        idempotencyKey: `engagement-confirm-${data.email}-${data.submittedAt}`,
        templateData: {
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          phone: data.phone,
          company: data.company,
          role: data.role,
          preferredContact: data.preferredContact,
          smsConsent: data.smsConsent,
          engagementType: data.engagementType,
          eventName: data.eventName,
          eventDates: data.eventDates,
          location: data.location,
          audienceSize: data.audienceSize,
          sessionLength: data.sessionLength,
          budgetRange: data.budgetRange,
          audienceTakeaway: data.audienceTakeaway,
          constraints: data.constraints,
          howHeard: data.howHeard,
        },
      });
      if (!confirm.ok) {
        console.warn("Engagement confirmation enqueue skipped", { reason: confirm.reason });
      }
    } catch (err) {
      console.warn("Engagement confirmation enqueue threw", {
        error: err instanceof Error ? err.message : String(err),
      });
    }

    return result;
  });
